import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, Skeleton } from "../components/ui";
import { fetchBlogs, fetchMyBlogs } from "../lib/api";
import { assetUrl } from "../lib/assets";
import { trackEvent } from "../lib/analytics";
import { resolvePublicAuthorName } from "../lib/authorNames";
import { estimateBlogReadingTimeLabel, resolveBlogDisplayStyle, resolveBlogHeroMedia, resolveMediaAltText } from "../lib/blogPosts";
import { useShellSession } from "../hooks/useShellSession";
import type { BlogPost } from "../types";

interface DietitianBlogsPageProps {
  mine?: boolean;
  basePath?: string;
  publicView?: boolean;
}

function toStatusLabel(status?: string | null): string {
  const source = (status || "").trim();
  if (!source) {
    return "PUBLISHED";
  }
  return source.replace(/_/g, " ").toUpperCase();
}

function formatDate(value?: string | null): string {
  if (!value) {
    return "Unknown";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Unknown";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(parsed);
}

export function DietitianBlogsPage({
  mine = false,
  basePath = "/app/dietitian-says",
  publicView = false,
}: DietitianBlogsPageProps) {
  const { session } = useShellSession();
  const isDietitian = Boolean(session.user?.isDietitian);
  const isAuthenticated = Boolean(session.isAuthenticated);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [drafts, setDrafts] = useState<BlogPost[]>([]);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      if (mine) {
        const myPayload = await fetchMyBlogs(30, 0);
        setBlogs(myPayload.items ?? []);
        setDrafts((myPayload.items ?? []).filter((blog) => (blog.status || "").toUpperCase() === "DRAFT"));
      } else {
        const [feedPayload, myPayload] = await Promise.all([
          fetchBlogs(18, 0),
          isDietitian ? fetchMyBlogs(8, 0) : Promise.resolve({ items: [] }),
        ]);
        setBlogs(feedPayload.items ?? []);
        setDrafts((myPayload.items ?? []).filter((blog) => (blog.status || "").toUpperCase() === "DRAFT"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load dietitian posts.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [mine, isDietitian]);

  const visibleBlogs = useMemo(() => {
    const source = blogs ?? [];
    if (publicView) {
      return source.filter((blog) => (blog.status || "").toUpperCase() === "PUBLISHED");
    }
    if (isDietitian) {
      return source;
    }
    return source.filter((blog) => (blog.status || "").toUpperCase() === "PUBLISHED");
  }, [blogs, isDietitian, publicView]);

  const featured = !mine ? visibleBlogs[0] ?? null : null;
  const listItems = !mine ? visibleBlogs.slice(1) : visibleBlogs;
  const publishedCount = visibleBlogs.filter((blog) => (blog.status || "").toUpperCase() === "PUBLISHED").length;
  const draftCount = visibleBlogs.filter((blog) => (blog.status || "").toUpperCase() === "DRAFT").length;
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const signInBase = session.userLoginUrl || session.loginUrl || "/app/auth/sign-in/user/";
  const signInSeparator = signInBase.includes("?") ? "&" : "?";
  const signInUrl = `${signInBase}${signInSeparator}next=${encodeURIComponent(currentPath)}`;
  const feedPath = basePath;
  const draftsPath = `${basePath}/mine`;
  const newPostPath = `${basePath}/new`;

  if (mine && !isDietitian) {
    return (
      <div data-testid="dietitian-blogs-page" className="space-y-4 pb-4">
        <PageHeader title="My dietitian posts" subtitle="Dietitian access is required to author and manage editorial posts." />
        <ErrorState message="Your account does not have dietitian publishing permissions." />
      </div>
    );
  }

  return (
    <div data-testid="dietitian-blogs-page" className="space-y-4 pb-4">
      <PageHeader
        title={mine ? "My dietitian posts" : "Dietitian says"}
        subtitle={
          mine
            ? "Draft, refine, and publish nutrition guidance for PantryRx members."
            : "Expert-reviewed editorial guidance designed for pantry-first nutrition decisions."
        }
        right={
          isDietitian && !publicView ? (
            <div className="flex flex-wrap gap-2">
              <Link to={mine ? feedPath : draftsPath} className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
                {mine ? "Open feed" : "My drafts"}
              </Link>
              <Link to={newPostPath} className="pr-btn-primary inline-flex min-h-10 items-center px-3 text-sm font-semibold" onClick={() => trackEvent("blog_write_clicked", { source: mine ? "my_posts" : "feed" })}>
                Write post
              </Link>
            </div>
          ) : publicView && !isAuthenticated ? (
            <a href={signInUrl} className="pr-btn-primary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
              Sign in to engage
            </a>
          ) : null
        }
      />

      {mine ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Card flat>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Drafts</p>
            <p className="pr-display mt-2 text-3xl font-semibold">{draftCount}</p>
          </Card>
          <Card flat>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Published</p>
            <p className="pr-display mt-2 text-3xl font-semibold">{publishedCount}</p>
          </Card>
        </div>
      ) : null}

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-52" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : visibleBlogs.length === 0 ? (
        <EmptyState
          title={mine ? "No posts yet" : "No dietitian stories yet"}
          subtitle={
            mine
              ? "Create your first post to publish practical pantry recommendations."
              : "New dietitian articles will appear here as soon as they are published."
          }
          image={assetUrl("ai-insights-hero.png")}
          action={
            isDietitian && !publicView ? (
              <Link to={newPostPath} className="pr-btn-primary inline-flex min-h-10 items-center px-4 text-sm font-semibold">
                Write your first post
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-3">
            {featured ? (
              <Card className="overflow-hidden p-0">
                <div className="relative h-64 sm:h-72">
                  <img
                    src={resolveBlogHeroMedia(featured)?.url || assetUrl("pantry-inventory-bg.png")}
                    alt={resolveMediaAltText(resolveBlogHeroMedia(featured), featured.title)}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-black/15" />
                  <div className="absolute inset-0 flex flex-col justify-end p-5 text-white">
                    <div className="flex flex-wrap items-center gap-2">
                      <Pill tone="accent" label="FEATURED" />
                      {resolveBlogDisplayStyle(featured) === "RECIPE" ? <Pill tone="accent" label="RECIPE" /> : null}
                      {featured.tags.slice(0, 3).map((tag) => (
                        <Pill key={tag} label={tag} />
                      ))}
                    </div>
                    <h2 className="pr-display mt-3 text-2xl font-semibold sm:text-3xl">{featured.title}</h2>
                    <p className="mt-1 line-clamp-2 text-sm text-white/90">{featured.summary || "Read this week’s featured post."}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-white/85">
                      <span>{resolvePublicAuthorName(featured)}</span>
                      <span>{formatDate(featured.published_at || featured.created_at)}</span>
                      <span>{estimateBlogReadingTimeLabel(featured)}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link to={`${basePath}/${featured.id}`} className="pr-btn-primary inline-flex min-h-10 items-center px-3 text-sm font-semibold" onClick={() => trackEvent("blog_post_clicked", { blog_id: featured.id, source: "featured" })}>
                        Read featured post
                      </Link>
                      {isDietitian && !publicView ? (
                        <Link to={`${basePath}/${featured.id}/edit`} className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
                          Edit
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </div>
              </Card>
            ) : null}

            {listItems.map((blog) => (
              <Card key={blog.id} flat>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap gap-2">
                      {resolveBlogDisplayStyle(blog) === "RECIPE" ? <Pill tone="accent" label="RECIPE" /> : null}
                      {blog.tags.slice(0, 3).map((tag) => (
                        <Pill key={`${blog.id}-${tag}`} label={tag} />
                      ))}
                      {(blog.status || "").toUpperCase() === "DRAFT" ? <Pill tone="accent" label="DRAFT" /> : null}
                    </div>
                    <h3 className="mt-2 text-lg font-semibold">
                      <Link className="hover:underline" to={`${basePath}/${blog.id}`}>
                        {blog.title}
                      </Link>
                    </h3>
                    <p className="mt-1 line-clamp-3 text-sm text-[var(--pr-text-muted)]">
                      {blog.summary || "No summary provided yet for this post."}
                    </p>
                    <p className="mt-2 text-xs text-[var(--pr-text-muted)]">
                      {resolvePublicAuthorName(blog)} · {formatDate(blog.published_at || blog.created_at)} ·{" "}
                      {estimateBlogReadingTimeLabel(blog)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link to={`${basePath}/${blog.id}`} className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold" onClick={() => trackEvent("blog_post_clicked", { blog_id: blog.id, source: "feed_list" })}>
                      Read
                    </Link>
                    {isDietitian && !publicView ? (
                      <Link to={`${basePath}/${blog.id}/edit`} className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold">
                        Edit
                      </Link>
                    ) : null}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {!mine ? (
            <div className="space-y-3">
              <Card flat>
                <h3 className="text-base font-semibold">Today</h3>
                <p className="mt-2 text-sm text-[var(--pr-text-muted)]">
                  Apply one pantry swap from a post this week and compare your next scan score.
                </p>
              </Card>
              <Card flat>
                <h3 className="text-base font-semibold">Trending tags</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Array.from(new Set(visibleBlogs.flatMap((blog) => blog.tags))).slice(0, 10).map((tag) => (
                    <Pill key={tag} label={tag} />
                  ))}
                  {visibleBlogs.every((blog) => blog.tags.length === 0) ? (
                    <p className="text-sm text-[var(--pr-text-muted)]">New posts will populate tags here.</p>
                  ) : null}
                </div>
              </Card>
              {isDietitian ? (
                <Card flat>
                  <h3 className="text-base font-semibold">Drafts</h3>
                  {drafts.length === 0 ? (
                    <p className="mt-2 text-sm text-[var(--pr-text-muted)]">No drafts in progress.</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {drafts.slice(0, 4).map((blog) => (
                        <Link
                          key={blog.id}
                          to={`/app/dietitian-says/${blog.id}/edit`}
                          className="block rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                        >
                          <p className="font-semibold">{blog.title}</p>
                          <p className="text-xs text-[var(--pr-text-muted)]">{formatDate(blog.updated_at || blog.created_at)}</p>
                        </Link>
                      ))}
                    </div>
                  )}
                </Card>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
